// ── ADMIN: ARCHIVING (families and performers) ───────────────────────
// The middle ground between leaving a departed family in every roster
// forever and scripts/remove_family.php's hard delete. Archiving hides
// people from the working views and leaves every row in place, so a
// performer who comes back next season reconnects to their own account,
// charges, and contract history instead of arriving as a new family.
//
// The one rule the server enforces and this file mirrors, so the reason is
// visible before the click rather than after the error: an unpaid balance
// blocks archiving (Archive::accountUnpaidTotal). Archiving and restoring are
// otherwise both ordinary full-admin actions.
//
// Callers are admin-accounts.js (parent detail) and admin-students.js
// (roster + performer detail), both via data-action wiring in their own
// VIEW_INIT hooks — nothing here goes on window.
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS, session } from '../core/state.js';
import { fmtLocalDT } from '../core/format.js';
import { canEdit } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { route, refreshAdminData } from '../core/routing.js';

// Offered as one-click reasons because they're the four that actually come up,
// and a reason typed six months ago is what tells Lori whether a returning
// family is a welcome-back or a conversation.
const REASONS = [
  'Moved away',
  'Aged out',
  'Taking a season off',
  'Left the program',
];

const reasonField = id => `
  <div class="field">
    <label>Reason <span style="color:var(--muted);font-weight:400">(optional, shown when they come back)</span></label>
    <select id="${id}Preset" style="height:38px;margin-bottom:8px">
      <option value="">Choose a reason…</option>
      ${REASONS.map(r => `<option value="${esc(r)}">${esc(r)}</option>`).join('')}
      <option value="__other">Something else…</option>
    </select>
    <input type="text" id="${id}Text" placeholder="Add a note" style="height:38px;display:none">
  </div>`;

function wireReasonField(id) {
  const preset = document.getElementById(id + 'Preset');
  const text   = document.getElementById(id + 'Text');
  preset.addEventListener('change', () => {
    const other = preset.value === '__other';
    text.style.display = other ? 'block' : 'none';
    if (other) text.focus();
  });
}

function readReason(id) {
  const preset = document.getElementById(id + 'Preset').value;
  const text   = document.getElementById(id + 'Text').value.trim();
  if (preset === '__other') return text || null;
  return preset || null;
}

// Client-side mirror of Archive::accountUnpaidTotal / performerUnpaidTotal.
// 'failed' counts as unpaid — it's still owed.
const isUnpaid = c => c.status === 'due' || c.status === 'failed';
const familyDue = accountId =>
  DB.charges.filter(c => c.accountId === accountId && isUnpaid(c)).reduce((s, c) => s + c.amount, 0);
// Account-level charges (no performerId) belong to the family, not to one
// sibling, so they don't block a single performer's archive — same split the
// server makes.
const performerDue = performerId =>
  DB.charges.filter(c => c.performerId === performerId && isUnpaid(c)).reduce((s, c) => s + c.amount, 0);

const blockedByBalance = (due, who) => `
  <h3>Can't archive ${esc(who)} yet</h3>
  <p class="card-intro">
    There's an outstanding balance of <b style="color:var(--red)">${fmt$(due)}</b>.
    Archiving hides a family from Billing along with everything else, so a balance
    would disappear with no way left to surface it. Charge the card, or waive the
    charges, and then archive.
  </p>
  <button class="btn" onclick="closeModal()" style="width:100%">Got it</button>`;

// Client-side mirror of Archive::enrollmentBlocker. Archiving stops billing but
// charges no cancellation fee, so anyone still enrolled in a billed offering
// (PC/BB, or a Tap & Voice class) this season or a later one has to be dropped
// first. A season starts September 1 of its seasonYear, so before September the
// live season is last year's -- the same date rule the server uses.
const liveSeason = () => { const d = new Date(); return d.getFullYear() - (d.getMonth() >= 8 ? 0 : 1); };
function stillEnrolled(performerIds) {
  return DB.seasonEnrollments
    .filter(se => performerIds.includes(se.performerId) && se.status === 'registered' && se.seasonYear >= liveSeason())
    .map(se => ({ se, k: DB.children.find(c => c.id === se.performerId), p: PROGRAMS.find(x => x.id === se.programId) }))
    .filter(({ k, p }) => k && p && (p.isProgram || p.workshopGroup === 'tap_voice'));
}

const blockedByEnrollment = (rows, who) => `
  <h3>Can't archive ${esc(who)} yet</h3>
  <p class="card-intro">
    Archiving stops billing, but it doesn't charge a cancellation fee or end the
    enrollment. Take ${rows.length > 1 ? 'these' : 'this'} off first, then archive:
  </p>
  <ul style="margin:0 0 16px;padding-left:20px;font-size:13.5px;line-height:1.7;color:var(--cream)">
    ${rows.map(({ k, p }) => p.isProgram
      ? `<li>Drop ${esc(k.name)} from ${esc(p.name)} <span style="color:var(--muted)">(Performers → ${esc(k.name)} → Drop)</span></li>`
      : `<li>Remove ${esc(k.name)} from the ${esc(p.name)} roster <span style="color:var(--muted)">(Classes → Roster → Remove)</span></li>`).join('')}
  </ul>
  <button class="btn" onclick="closeModal()" style="width:100%">Got it</button>`;

// ── Archive ─────────────────────────────────────────────────────────────────

export function archiveFamilyModal(accountId) {
  const u = DB.users.find(x => x.id === accountId);
  if (!u) return;

  const due = familyDue(accountId);
  if (due > 0) { openModal(blockedByBalance(due, u.firstName + ' ' + u.lastName)); return; }

  const enrolled = stillEnrolled(DB.children.filter(c => c.accountId === accountId).map(c => c.id));
  if (enrolled.length) { openModal(blockedByEnrollment(enrolled, u.firstName + ' ' + u.lastName)); return; }

  const kids = DB.children.filter(c => c.accountId === accountId && !c.archivedAt);

  openModal(`
    <h3>Archive ${esc(u.firstName)} ${esc(u.lastName)}</h3>
    <p class="card-intro">
      They'll come out of the Parents and Performers lists, the class rosters, the
      birthday digest, and the store trackers, and they won't be able to sign in.
      <b style="color:var(--cream)">Nothing is deleted</b> — enrollments, payment
      history, and signed contracts all stay exactly where they are.
    </p>
    ${kids.length ? `
      <div style="padding:10px 14px;background:rgba(201,168,76,0.06);border:1px solid rgba(201,168,76,0.25);border-radius:7px;margin-bottom:14px">
        <div class="detail-label">Archived along with the account</div>
        <div style="font-size:13px;color:var(--cream)">${kids.map(k => esc(k.name)).join(', ')}</div>
      </div>` : ''}
    ${reasonField('afm')}
    <p style="font-size:11px;color:var(--muted);margin:2px 0 14px;line-height:1.6">
      If they register again, signing the contract brings the whole family back
      automatically — same account, same history. You can also restore them by
      hand any time, from the Archived filter on the Parents list.
    </p>
    <div style="display:flex;gap:10px">
      <button class="btn-ghost" onclick="closeModal()" style="flex:1">Cancel</button>
      <button class="btn-ghost btn-danger" id="afmConfirm" style="flex:1">Archive family</button>
    </div>`);

  wireReasonField('afm');
  document.getElementById('afmConfirm').addEventListener('click', async () => {
    const btn = document.getElementById('afmConfirm');
    btn.disabled = true;
    try {
      const res = await apiFetch('/accounts/' + accountId + '/archive', {
        method: 'POST',
        body: JSON.stringify({ reason: readReason('afm') }),
      });
      closeModal();
      await refreshAdminData(true);
      route('a-accounts');
      toast(res.message || 'Family archived.');
    } catch (e) { btn.disabled = false; toastError(e.message); }
  });
}

export function archivePerformerModal(performerId) {
  const k = DB.children.find(c => c.id === performerId);
  if (!k) return;

  const due = performerDue(performerId);
  if (due > 0) { openModal(blockedByBalance(due, k.name)); return; }

  const enrolled = stillEnrolled([performerId]);
  if (enrolled.length) { openModal(blockedByEnrollment(enrolled, k.name)); return; }

  const siblings = DB.children.filter(c => c.accountId === k.accountId && c.id !== k.id && !c.archivedAt);

  openModal(`
    <h3>Archive ${esc(k.name)}</h3>
    <p class="card-intro">
      They'll come out of the Performers list, the class rosters, the birthday
      digest, and the store trackers. <b style="color:var(--cream)">Nothing is
      deleted</b>, and the parent account stays active${siblings.length
        ? ` — ${esc(siblings.map(s => s.name).join(' and '))} ${siblings.length > 1 ? 'are' : 'is'} unaffected.`
        : '.'}
    </p>
    ${reasonField('apm')}
    <p style="font-size:11px;color:var(--muted);margin:2px 0 14px;line-height:1.6">
      If they register again, signing the contract restores this same performer
      record automatically. You can also restore them by hand any time, from the
      Archived filter on the Performers list.
    </p>
    <div style="display:flex;gap:10px">
      <button class="btn-ghost" onclick="closeModal()" style="flex:1">Cancel</button>
      <button class="btn-ghost btn-danger" id="apmConfirm" style="flex:1">Archive performer</button>
    </div>`);

  wireReasonField('apm');
  document.getElementById('apmConfirm').addEventListener('click', async () => {
    const btn = document.getElementById('apmConfirm');
    btn.disabled = true;
    try {
      const res = await apiFetch('/performers/' + performerId + '/archive', {
        method: 'POST',
        body: JSON.stringify({ reason: readReason('apm') }),
      });
      closeModal();
      await refreshAdminData(true);
      route('a-students');
      toast(res.message || 'Performer archived.');
    } catch (e) { btn.disabled = false; toastError(e.message); }
  });
}

// ── Un-archive ──────────────────────────────────────────────────────────────

function unarchiveModal({ title, body, onConfirm }) {
  openModal(`
    <h3>${title}</h3>
    <p class="card-intro">${body}</p>
    <div style="display:flex;gap:10px">
      <button class="btn-ghost" onclick="closeModal()" style="flex:1">Cancel</button>
      <button class="btn" id="uamConfirm" style="flex:1">Restore</button>
    </div>`);

  document.getElementById('uamConfirm').addEventListener('click', async () => {
    const btn = document.getElementById('uamConfirm');
    btn.disabled = true;
    try {
      const res = await onConfirm();
      closeModal();
      await refreshAdminData(true);
      toast(res.message || 'Restored.');
    } catch (e) { btn.disabled = false; toastError(e.message); }
  });
}

export function unarchiveFamily(accountId) {
  const u = DB.users.find(x => x.id === accountId);
  if (!u) return;

  unarchiveModal({
    title: `Restore ${esc(u.firstName)} ${esc(u.lastName)}`,
    body: `They'll reappear in the Parents and Performers lists and be able to sign in
           again with their existing password. Everyone archived with them
           ${u.archivedAt ? `on ${fmtLocalDT(u.archivedAt)} ` : ''}comes back too.`,
    onConfirm: () => apiFetch('/accounts/' + accountId + '/unarchive', { method: 'POST' }),
  });
}

export function unarchivePerformer(performerId) {
  const k = DB.children.find(c => c.id === performerId);
  if (!k) return;

  unarchiveModal({
    title: `Restore ${esc(k.name)}`,
    body: `They'll reappear in the Performers list and the rosters. If their family is
           archived too, restoring the performer brings the account back with them —
           a visible performer on an account that can't sign in helps no one.`,
    onConfirm: () => apiFetch('/performers/' + performerId + '/unarchive', { method: 'POST' }),
  });
}

// ── Shared bits used by the detail pages ────────────────────────────────────

export const archivedPill = () =>
  '<span class="pill pill-off" style="font-size:10px">Archived</span>';

// The Current / Archived / All filter the class rosters share with the
// Performers list: archived records are hidden by default, never unfindable.
export function filterArchived(rows, show) {
  if (show === 'archived') return rows.filter(r => r.archivedAt);
  if (show === 'all') return rows;
  return rows.filter(r => !r.archivedAt);
}
export function archivedShowSelect(id, show, archivedCount) {
  if (!archivedCount && show === 'current') return '';
  const opt = (v, label) => `<option value="${v}" ${show === v ? 'selected' : ''}>${label}</option>`;
  return `<select id="${id}" style="height:30px;width:auto;font-size:12px;margin-left:auto" aria-label="Show">
    ${opt('current', 'Current')}${opt('archived', `Archived (${archivedCount})`)}${opt('all', 'All')}
  </select>`;
}

// The banner that replaces the usual page actions on an archived record.
export function archivedBanner(record, { kind, onRestoreAction }) {
  const reason = record.archiveReason ? ` · ${esc(record.archiveReason)}` : '';
  return `
    <div class="card" style="margin-bottom:18px;border-color:rgba(201,168,76,0.35);display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap">
      <div>
        <div style="font-family:'Libre Baskerville',serif;font-size:17px;color:var(--cream)">This ${kind} is archived</div>
        <div style="font-size:13px;color:var(--muted);margin-top:3px">
          Archived ${fmtLocalDT(record.archivedAt)}${reason}. Hidden from the rosters and
          digests; nothing has been deleted.
        </div>
      </div>
      ${canEdit(session)
        ? `<button class="btn" style="width:auto;white-space:nowrap;padding:11px 22px;flex-shrink:0" data-action="${onRestoreAction}" data-id="${record.id}">Restore</button>`
        : ''}
    </div>`;
}
